import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { requests } from "../constants/requests";
import { noop } from "../redux/api";
import { deleteExpense, deletePayment } from "../redux/reducer/app-slice";
import { confirmDialog } from "../utils/confirm-dialog";
import { isIncome, isPayment, participantName } from "../utils/expense";
import { queuedText } from "../utils/outbox";
import { vibrate } from "./use-haptics";
import { useIsPending } from "./use-is-pending";
import { useToast } from "./use-toast";

export const UNDO_TIMEOUT_MS = 5000;
export const UNDO_HOLD_MS = UNDO_TIMEOUT_MS + 2000;
export const UNDO_TEXT = "Rückgängig";
export const UNDO_TOO_LATE_TEXT =
  "Das Löschen lässt sich nicht mehr rückgängig machen.";

const wordingFor = (entry, participants) =>
  isPayment(entry)
    ? {
        title: "Zahlung löschen",
        text: `Möchtest du die Zahlung von ${participantName(
          participants,
          entry.payer,
        )} an ${participantName(
          participants,
          entry.receivers?.[0]?.participant_id,
        )} wirklich löschen?`,
        cssClass: "delete-payment-dialog",
        deletedText: "Zahlung gelöscht",
        deleteEntry: deletePayment,
      }
    : isIncome(entry)
    ? {
        title: "Einnahme löschen",
        text: `Möchtest du die Einnahme „${entry.name ?? ""}“ wirklich löschen?`,
        cssClass: "delete-expense-dialog",
        deletedText: "Einnahme gelöscht",
        deleteEntry: deleteExpense,
      }
    : {
        title: "Ausgabe löschen",
        text: `Möchtest du „${entry.name ?? ""}“ wirklich löschen?`,
        cssClass: "delete-expense-dialog",
        deletedText: "Ausgabe gelöscht",
        deleteEntry: deleteExpense,
      };

export const useDeleteEntry = () => {
  const dispatch = useDispatch();
  const { showToast, showError } = useToast();
  const isDeleting = useIsPending(requests.deleteExpense);

  const showUndo = useCallback(
    (text, { undo, release }) => {
      let undone = false;
      let toast = null;
      const closeWhenHidden = () => {
        if (document.visibilityState !== "visible") toast?.close();
      };
      toast = showToast({
        text,
        closeTimeout: UNDO_TIMEOUT_MS,
        cssClass: "undo-toast",
        buttonText: UNDO_TEXT,
        on: {
          open: () =>
            document.addEventListener("visibilitychange", closeWhenHidden),
          closeButtonClick: () => {
            undone = true;
            if (!dispatch(undo)) showError(UNDO_TOO_LATE_TEXT);
          },
          close: () => {
            document.removeEventListener("visibilitychange", closeWhenHidden);
            if (!undone) dispatch(release);
          },
        },
      });
    },
    [dispatch, showToast, showError],
  );

  const deleteNow = useCallback(
    (entry, participants, onDeleted) => {
      const { deletedText, deleteEntry } = wordingFor(entry, participants);
      dispatch(
        deleteEntry(
          entry.id,
          (group, { queued = false, undo, release } = {}) => {
            vibrate();
            const text = queued ? queuedText(deletedText) : deletedText;
            if (undo) showUndo(text, { undo, release });
            else showToast({ text, closeTimeout: 1500 });
            onDeleted();
          },
          showError,
          { holdMs: UNDO_HOLD_MS },
        ),
      );
    },
    [dispatch, showUndo, showToast, showError],
  );

  const remove = useCallback(
    (entry, participants = [], onDeleted = noop, onClosed) => {
      if (!entry?.id || isDeleting) {
        onClosed?.();
        return;
      }
      const { title, text, cssClass } = wordingFor(entry, participants);
      confirmDialog({
        title,
        text,
        cssClass,
        confirmText: "Löschen",
        destructive: true,
        onClosed,
        onConfirm: () => deleteNow(entry, participants, onDeleted),
      });
    },
    [isDeleting, deleteNow],
  );

  const removeWithoutConfirm = useCallback(
    (entry, participants = [], onDeleted = noop) => {
      if (!entry?.id || isDeleting) return;
      deleteNow(entry, participants, onDeleted);
    },
    [isDeleting, deleteNow],
  );

  return { isDeleting, remove, removeWithoutConfirm };
};
